import { z } from 'zod';
import { BaseMessage } from '@langchain/core/messages';

export const zFormatResultMetaSchema = z.object({
	name: z.string()
		.describe('A short 3-5 word title summarizing the entire conversation. This will be used as the chat name.'),
	nextSteps: z.array(z.string()).nullable()
		.describe(
			'Up to 5 ready-to-send user prompts reflecting the CURRENT state of the conversation. ' +
			'If the coordinator presented choices (numbered list, bullets, or "one of these" phrasing), emit EACH choice as a separate entry — never merge them into one string with embedded "1/2/3" options. ' +
			'For completed tasks: suggest specific follow-ups on THAT task (e.g., ["Add a decorative background", "Make avatars larger"]). ' +
			'Do NOT include suggestions from earlier topics the user has moved past — once the user changes subject, prior offers are stale. ' +
			'Never use vague labels like "Choose a style" or "Something else".'
		),
	assets: z.array(z.string())
		.describe(
			'Identify the primary asset(s) created (asset ids) during the last interaction with the user. A single asset. On very rare occasions if the user prompts to create multiple assets at once and none of them is the main one then select several, e.g. when the user asks to modify all avatars but not asks to create a report with them. Only the exact ids of the assets.'
		),
	mainAssetId: z.string().nullable()
		.describe('The single most important asset id produced. Prefer final composed output (visjs graph or SVG) over intermediate assets (backgrounds, avatars).'),
}).describe('Summarize the user interaction with the AI.');

export type FormatResultMetaType = z.infer<typeof zFormatResultMetaSchema>;

export type DeepAgentStreamState = {
	messages: BaseMessage[];
	summary: FormatResultMetaType | null;
	/**
	 * True when this thread's last run crashed and a manual retry is pending. Returned by the
	 * thread-fetch endpoint so the "Try again" affordance survives reload/navigation.
	 */
	pending_retry?: boolean;
};

export interface SubagentResult {
	summary: string;
	assets: string[];
}

export type StreamCrashEventType = 'agent_crashed';

// Framework-internal tools whose call/result must never render in the chat UI.
export const INTERNAL_TOOL_NAMES = new Set([
	'task',
	'ls',
	'read_file',
	'write_file',
	'edit_file',
	'glob',
	'grep',
	'execute',
	'start_async_task',
	'check_async_task',
	'update_async_task',
	'cancel_async_task',
	'list_async_tasks',
]);

export function parseSubagentResult(content: unknown): SubagentResult | null {
	if (typeof content !== 'string') return null;
	try {
		const parsed = JSON.parse(content);
		if (typeof parsed?.summary === 'string' && Array.isArray(parsed?.assets)) {
			return parsed as SubagentResult;
		}
		return null;
	} catch {
		return null;
	}
}